import { useQueryClient } from '@tanstack/react-query';
import { LockKeyhole, ShieldCheck } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { demoStepUp } from '../lib/demo-state';
import { useM, useQ } from '../lib/hooks';
import { authCall, RpcError } from '../lib/rpc';
import { Button, Checkbox, ErrorBox, Field, Input, Loading, Panel, Select, useToast } from './ui';

/**
 * Additional confirmation for Abrechnung and Kunden (request 31 of round 4). With the setting on, the module opens only
 * after the person confirmed again within the set minutes: the authenticator code when one is set up, otherwise the
 * password (demo: the word "demo"). The server checks it on every call of the module; this gate only asks for it.
 */
export function StepUpGate({ area, children }: { area: 'billing' | 'customers'; children: ReactNode }) {
  const q = useQ('me.stepUp', {}, { refetchInterval: 60_000 });
  if (q.isLoading) return <Loading />;
  if (!q.data || !q.data[area] || q.data.fresh) return <>{children}</>;
  return <StepUpPanel area={area} minutes={q.data.minutes} />;
}

function StepUpPanel({ area, minutes }: { area: 'billing' | 'customers'; minutes: number }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [method, setMethod] = useState<'password' | 'totp' | null | undefined>(__DEMO__ ? 'password' : undefined);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    if (__DEMO__) return;
    authCall<{ stepUpMethod?: 'password' | 'totp' | null }>('session', undefined, 'GET')
      .then((s) => setMethod(s.stepUpMethod ?? null))
      .catch(() => setMethod(null));
  }, []);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (__DEMO__) {
        if (!demoStepUp(value)) throw new RpcError('INVALID_CREDENTIALS');
      } else {
        await authCall('step-up', method === 'totp' ? { totp: value.trim() } : { password: value });
      }
      setValue('');
      // the whole module was refused before: everything is loaded again
      await qc.invalidateQueries();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mx-auto mt-10 w-full max-w-md">
      <Panel title={t('stepUp.title')}>
        <div className="flex flex-col gap-3">
          <p className="flex items-start gap-2 text-sm text-graphite">
            <LockKeyhole className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden />
            {t('stepUp.text', { module: t(`nav.${area}`), minutes })}
          </p>
          {method === undefined && <Loading />}
          {method === null && <p className="text-sm text-warn">{t('stepUp.unavailable')}</p>}
          {method && (
            <form
              className="flex flex-col gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
            >
              <Field label={method === 'totp' ? t('stepUp.totp') : t('stepUp.password')} hint={__DEMO__ ? t('stepUp.demoHint') : undefined}>
                <Input
                  autoFocus
                  type={method === 'totp' ? 'text' : 'password'}
                  inputMode={method === 'totp' ? 'numeric' : undefined}
                  autoComplete={method === 'totp' ? 'one-time-code' : 'current-password'}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                />
              </Field>
              <ErrorBox error={error} />
              <Button type="submit" variant="primary" loading={busy} disabled={!value.trim()} icon={<ShieldCheck className="size-4" />}>
                {t('stepUp.confirm')}
              </Button>
            </form>
          )}
          <p className="text-xs text-graphite">{t('stepUp.limits')}</p>
        </div>
      </Panel>
    </div>
  );
}

/** Switches the confirmation per module (holders of the module right) and sets how long it holds. */
export function StepUpSettings() {
  const { t } = useTranslation();
  const toast = useToast();
  const q = useQ('me.stepUp', {});
  const set = useM('security.setStepUp', { invalidate: ['me.stepUp'] });
  if (!q.data || (!q.data.canChange.billing && !q.data.canChange.customers)) return null;
  const d = q.data;
  const save = (patch: { billing?: boolean; customers?: boolean; minutes?: number }) => set.mutateAsync(patch).then(() => toast.ok(t('common.saved'))).catch(toast.error);
  return (
    <details className="rounded-lg border border-line bg-sheet px-3 py-2 text-sm">
      <summary className="cursor-pointer font-medium select-none">
        <LockKeyhole className="mr-1 inline size-4 text-graphite" aria-hidden />
        {t('stepUp.settingsTitle')}
      </summary>
      <div className="mt-2 flex flex-col gap-2">
        {d.canChange.billing && <Checkbox label={t('stepUp.forBilling')} checked={d.billing} onChange={(e) => void save({ billing: e.target.checked })} />}
        {d.canChange.customers && <Checkbox label={t('stepUp.forCustomers')} checked={d.customers} onChange={(e) => void save({ customers: e.target.checked })} />}
        <Field label={t('stepUp.minutes')} className="w-48">
          <Select value={d.minutes} onChange={(e) => void save({ minutes: Number(e.target.value) })}>
            {[5, 15, 30, 60, 120, 240].map((m) => (
              <option key={m} value={m}>
                {t('stepUp.minutesValue', { count: m })}
              </option>
            ))}
          </Select>
        </Field>
        <p className="text-xs text-graphite">{t('stepUp.settingsHint')}</p>
        <p className="text-xs text-graphite">{t('stepUp.limits')}</p>
      </div>
    </details>
  );
}
